import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/shared/ui';
import { FileText, X } from 'lucide-react';
import { formatDateTime } from '@/utils/dateUtils';
import { Note } from '../../../types/admin';

interface AddNoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (note: any) => void | Promise<void>;
    editNote?: Note | null;
}

interface NoteFormData {
    content: string;
    documents: File[];
    existingAttachments: string[];
}

export default function AddNoteModal({ isOpen, onClose, onSave, editNote }: AddNoteModalProps): React.ReactElement {
    const { i18n } = useTranslation();
    const isEditMode = !!editNote;
    const [formData, setFormData] = useState<NoteFormData>({
        content: '',
        documents: [],
        existingAttachments: []
    });

    useEffect(() => {
        if (isOpen && editNote) {
            setFormData({
                content: editNote.content,
                documents: [],
                existingAttachments: editNote.attachments || []
            });
        } else if (isOpen && !editNote) {
            setFormData({
                content: '',
                documents: [],
                existingAttachments: []
            });
        }
    }, [isOpen, editNote]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            const validFiles = files.filter(file => {
                const ext = file.name.split('.').pop()?.toLowerCase() || '';
                return ['pdf', 'doc', 'docx', 'xlsx', 'xls', 'csv', 'ppt', 'pptx', 'txt', 'png', 'jpg', 'jpeg'].includes(ext);
            });

            setFormData(prev => ({
                ...prev,
                documents: [...prev.documents, ...validFiles]
            }));
            
            e.target.value = '';
        }
    };

    const removeDocument = (index: number) => {
        setFormData(prev => ({
            ...prev,
            documents: prev.documents.filter((_, i) => i !== index)
        }));
    };

    const removeExistingAttachment = (index: number) => {
        setFormData(prev => ({
            ...prev,
            existingAttachments: prev.existingAttachments.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const noteData = {
            id: isEditMode ? editNote.id : `NOTE${Date.now()}`,
            adminId: isEditMode ? editNote.adminId : 'admin@medicrew.com',
            adminName: isEditMode ? editNote.adminName : '관리자',
            createdAt: isEditMode ? editNote.createdAt : formatDateTime(new Date().toISOString(), i18n.language),
            content: formData.content,
            links: isEditMode ? (editNote.links || []) : [],
            attachments: [...formData.existingAttachments, ...formData.documents.map(f => f.name)],
            /** 부모에서 업로드 API로 전송 */
            attachmentFiles: formData.documents,
            retainedLinkUrls: isEditMode ? (editNote.links || []) : [],
            images: isEditMode ? editNote.images : []
        };

        try {
            await onSave(noteData);
            setFormData({ content: '', documents: [], existingAttachments: [] });
            onClose();
        } catch {
            /* 상위(저장 실패)에서 토스트 처리 */
        }
    };

    const footer = (
        <>
            <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={onClose}
            >
                취소
            </button>
            <button
                type="submit"
                form="add-note-form"
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
            >
                저장
            </button>
        </>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? "노트 수정" : "노트 추가"} footer={footer} size="large">
            <form id="add-note-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                        내용 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        name="content"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none"
                        value={formData.content}
                        onChange={handleChange}
                        required
                        placeholder="기관 메모를 입력하세요..."
                        rows={6}
                    />
                </div>

                {/* Document Upload */}
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">파일 첨부</label>
                    
                    {/* 기존 첨부파일 (수정 모드) */}
                    {isEditMode && formData.existingAttachments.length > 0 && (
                        <div className="mb-3">
                            <p className="text-xs text-gray-600 mb-2">기존 첨부파일</p>
                            <div className="space-y-2">
                                {formData.existingAttachments.map((filename, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200">
                                        <span className="flex items-center gap-2 text-sm text-gray-900">
                                            <FileText size={16} className="flex-shrink-0 text-blue-600" />
                                            {filename}
                                        </span>
                                        <button
                                            type="button"
                                            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                            onClick={() => removeExistingAttachment(index)}
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                        onClick={() => document.getElementById('note-document-input')?.click()}
                    >
                        <input
                            id="note-document-input"
                            type="file"
                            accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
                            multiple
                            onChange={handleDocumentChange}
                            className="hidden"
                        />
                        <FileText size={32} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">
                            클릭하여 문서 선택<br />
                            <span className="text-xs text-gray-500">지원 형식: PDF, Word, Excel, PowerPoint, TXT, png, jpg</span>
                        </p>
                    </div>

                    {formData.documents.length > 0 && (
                        <div className="mt-3">
                            <p className="text-xs text-gray-600 mb-2">새 첨부파일</p>
                            <div className="space-y-2">
                                {formData.documents.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                                        <span className="flex items-center gap-2 text-sm text-gray-900">
                                            <FileText size={16} className="flex-shrink-0" />
                                            {file.name}
                                        </span>
                                        <button
                                            type="button"
                                            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                            onClick={() => removeDocument(index)}
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>


            </form>
        </Modal >
    );
}
